import React, { useCallback, useEffect, useState, useRef } from "react";
import {
	Button,
	Col,
	FormGroup,
	Input,
	Modal,
	ModalBody,
	Row,
	Table,
} from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import indicadoresService from "services/IndicadoresService";
import indicadoresConfigurationService from "services/IndicadoresConfigurationService";
import InputNumber from "components/Common/InputNumber";
import { Calculator, SignOut } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import { generateYearOptions } from "helpers/yearHelper";
import { formatScaledIndicatorValue } from "helpers/indicatorValueHelper";
import { buildSpreadsheetCopyTsv } from "helpers/spreadsheetClipboardHelper";
import Spreadsheet from "react-spreadsheet";

// DataViewer: Exibe valores formatados (replica lógica do InputNumber)
const NumericDataViewer = ({ cell }) => {
	const decimals = cell.decimals ?? 2;
	const formatted = formatScaledIndicatorValue(cell.value, decimals);

	if (!formatted) return <span></span>;

	return (
		<span style={{ textAlign: "right", display: "block" }}>{formatted}</span>
	);
};

// DataEditor: Editor numérico customizado (replica InputNumber)
const NumericDataEditor = ({ cell, onChange }) => {
	const [localValue, setLocalValue] = React.useState('');
	const decimals = cell.decimals != null ? cell.decimals : 2;
	
	React.useEffect(() => {
		// Formatar valor inicial ao abrir editor
		const value = cell.value;
		if (!value && value !== 0) {
			setLocalValue('');
			return;
		}
		
		const isNegative = value.toString().startsWith("-");
		const cleanedValue = value.toString().replace(/[^\d]/g, "");
		
		if (cleanedValue === "") {
			setLocalValue(isNegative ? "-" : "");
			return;
		}
		
		if (decimals === 0) {
			const numValue = parseInt(cleanedValue, 10) || 0;
			const formatted = numValue.toLocaleString("pt-BR");
			setLocalValue((isNegative ? "-" : "") + formatted);
			return;
		}
		
		const intPart = cleanedValue.slice(0, -decimals) || "0";
		const decimalPart = cleanedValue.slice(-decimals).padStart(decimals, "0");
		const numValue = parseFloat(intPart + "." + decimalPart);
		
		const formatted = numValue.toLocaleString("pt-BR", {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
		
		setLocalValue((isNegative ? "-" : "") + formatted);
	}, [cell.value, decimals]);
	
	const handleChange = (e) => {
		const value = e.target.value;
		const isNegative = value.startsWith("-");
		const rawValue = value.replace(/[^\d-]/g, "").replace(/(?!^-)-/g, "");
		
		if (rawValue === "-") {
			setLocalValue("-");
			onChange({ ...cell, value: "" });
			return;
		}
		
		const numericString = rawValue.replace("-", "");
		
		if (numericString === "") {
			setLocalValue("");
			onChange({ ...cell, value: "" });
			return;
		}
		
		// Formatar visualmente
		let formatted = '';
		if (decimals === 0) {
			const numValue = parseInt(numericString, 10) || 0;
			formatted = numValue.toLocaleString("pt-BR");
		} else {
			const intPart = numericString.slice(0, -decimals) || "0";
			const decimalPart = numericString.slice(-decimals).padStart(decimals, "0");
			const numValue = parseFloat(intPart + "." + decimalPart);
			formatted = numValue.toLocaleString("pt-BR", {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});
		}
		
		setLocalValue((isNegative ? "-" : "") + formatted);
		
		// Salvar como inteiro (igual ao InputNumber)
		const numericValue = parseInt(numericString, 10) * (isNegative ? -1 : 1);
		onChange({ ...cell, value: numericValue });
	};
	
	return (
		<input
			type="text"
			value={localValue}
			onChange={handleChange}
			autoFocus
			className="Spreadsheet__data-editor"
			style={{ textAlign: 'right' }}
		/>
	);
};

export const months = [
	{ id: 1, label: "Jan" },
	{ id: 2, label: "Fev" },
	{ id: 3, label: "Mar" },
	{ id: 4, label: "Abr" },
	{ id: 5, label: "Mai" },
	{ id: 6, label: "Jun" },
	{ id: 7, label: "Jul" },
	{ id: 8, label: "Ago" },
	{ id: 9, label: "Set" },
	{ id: 10, label: "Out" },
	{ id: 11, label: "Nov" },
	{ id: 12, label: "Dez" },
];

export const years = generateYearOptions();

const Launch = ({
	show,
	onCloseClick,
	updateData,
	currentYear,
	currentLaunchId,
}) => {
	const [year, setYear] = useState(currentYear || new Date().getFullYear());
	const [formatedData, setFormatedData] = useState({});
	const [loading, setLoading] = useState();
	const [columnsData, setColumnsData] = useState({});
	const [columnsLoading, setColumnsLoading] = useState(false);
	const spreadsheetRef = useRef(null);
	const selectionRef = useRef(null);
	const spreadsheetDataRef = useRef([]);
	const columnLabelsRef = useRef([]);
	const rowLabelsRef = useRef([]);

	// Windows Excel Desktop appends trailing \r\n to clipboard text, which
	// react-spreadsheet's split() turns into a phantom empty row that overwrites
	// the row immediately below the paste target.  We intercept in the capture
	// phase, strip trailing line-breaks, and re-dispatch a clean event.
	useEffect(() => {
		if (!show) return undefined;

		let skipNextPaste = false;

		const handlePasteIntercept = (event) => {
			if (skipNextPaste) {
				skipNextPaste = false;
				return;
			}

			const root = spreadsheetRef.current;
			if (!root || !root.contains(document.activeElement)) return;

			const text = event.clipboardData?.getData('text/plain');
			if (!text) return;

			const cleanText = text.replace(/(\r\n|\r|\n)+$/, '');

			if (cleanText !== text) {
				event.preventDefault();
				event.stopImmediatePropagation();

				const dt = new DataTransfer();
				dt.setData('text/plain', cleanText);

				skipNextPaste = true;
				document.dispatchEvent(
					new ClipboardEvent('paste', {
						clipboardData: dt,
						bubbles: true,
						cancelable: true,
					}),
				);
			}

			setTimeout(() => setFormatedData((prev) => ({ ...prev })), 0);
		};

		const handleCopy = (event) => {
			const root = spreadsheetRef.current;
			if (!root || !root.matches(":focus-within")) return;

			const tsv = buildSpreadsheetCopyTsv({
				data: spreadsheetDataRef.current,
				selection: selectionRef.current,
				columnLabels: columnLabelsRef.current,
				rowLabels: rowLabelsRef.current,
			});
			if (!tsv) return;

			event.preventDefault();
			event.stopImmediatePropagation();
			event.clipboardData?.setData("text/plain", tsv);
		};

		document.addEventListener('paste', handlePasteIntercept, true);
		document.addEventListener("copy", handleCopy, true);
		return () => {
			document.removeEventListener('paste', handlePasteIntercept, true);
			document.removeEventListener("copy", handleCopy, true);
		};
	}, [show]);

	const handleSelect = useCallback((selected) => {
		selectionRef.current = selected;
	}, []);

	useEffect(() => {
		if (currentLaunchId) {
			setColumnsLoading(true);
			getColumnsConfig();
			getItems();
		}
	}, [year, currentLaunchId]);

	const getColumnsConfig = async () => {
		try {
			const response = await indicadoresConfigurationService.getAll(currentLaunchId);

			let columns = {};
			response.forEach((item) => {
				columns[item.id] = {
					id: item.id,
					name: item.name,
					quantifier: item.quantifier,
					sequence: parseInt(item.sequence) || 0,
					decimals_cases: item.decimals_cases,
					only_read: item.only_read === '1' || item.only_read === 1 || item.only_read === true
				};
			});

			setColumnsData(columns);
		} catch (error) {
			console.error("Erro ao buscar configurações das colunas:", error);
		} finally {
			setColumnsLoading(false);
		}
	};

	const getItems = async () => {
		try {
			const response = await indicadoresService.getLaunch(
				currentLaunchId,
				year,
			);

			let items = {};

			// Processar apenas os valores se existirem
			response.forEach((item) => {
				if (item.goals_config_values && item.goals_config_values.length > 0) {
					item.goals_config_values.forEach((field) => {
						if (!items[item.id]) {
							items[item.id] = {};
						}
						items[item.id][field.month] = field.value;
					});
				}
			});

			setFormatedData(items);
		} catch (error) {
			console.error("Erro ao buscar valores:", error);
		}
	};

	const formatNumber = (value) => {
		const number = parseFloat(value.replace(/[^0-9]/g, "")) / 100;
		return number.toLocaleString("pt-BR", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	const handleValueChange = (id, month, formattedValue) => {
		setFormatedData((prevValues) => ({
			...prevValues,
			[id]: {
				...prevValues[id],
				[month]: formattedValue,
			},
		}));
	};

	const onCalculateSave = async () => {
		try {
			if (currentLaunchId) {
				setLoading(true);
				const resp = await indicadoresService.updateLaunch(
					currentLaunchId,
					{ year, values: formatedData },
				);
				getItems();
				updateData();
				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Formulário calculado com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
		} catch (error) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: "Algo deu errado!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setLoading(false);
			onCloseClick();
		}
	};

	// Preparar dados para o Spreadsheet
	const columnLabels = Object.entries(columnsData)
		.sort((a, b) => a[1].sequence - b[1].sequence)
		.map(([id, coluna]) => `${coluna.name} (${coluna.quantifier})`);
	
	const rowLabels = months.map(month => month.label);
	
	// Criar matriz de dados: cada linha = mês, cada coluna = indicador
	const spreadsheetData = months.map((month) => {
		return Object.entries(columnsData)
			.sort((a, b) => a[1].sequence - b[1].sequence)
			.map(([id, coluna]) => {
				const raw = formatedData[coluna.id]?.[month.id];
				return {
					value: raw == null || raw === '' ? '' : raw,
					decimals: coluna.decimals_cases !== null && coluna.decimals_cases !== undefined && coluna.decimals_cases !== "" ? Number(coluna.decimals_cases) : 2,
					readOnly: coluna.only_read,
					DataViewer: NumericDataViewer,
					DataEditor: coluna.only_read ? undefined : NumericDataEditor,
				};
			});
	});

	spreadsheetDataRef.current = spreadsheetData;
	columnLabelsRef.current = columnLabels;
	rowLabelsRef.current = rowLabels;
	// Handler para mudanças no Spreadsheet
	const handleSpreadsheetChange = (newData) => {
		const updatedFormatedData = { ...formatedData };
		
		newData.forEach((row, monthIndex) => {
			const month = months[monthIndex];
			
			row.forEach((cell, colIndex) => {
				const sortedColumns = Object.entries(columnsData)
					.sort((a, b) => a[1].sequence - b[1].sequence);
				const [columnId, coluna] = sortedColumns[colIndex];
				
				// IMPORTANTE: Verificar se a coluna é readonly antes de permitir alteração
				if (coluna.only_read) {
					return; // Ignora mudanças em células readonly
				}
				
				if (cell.value !== '' && cell.value !== null && cell.value !== undefined) {
					if (!updatedFormatedData[columnId]) {
						updatedFormatedData[columnId] = {};
					}
					updatedFormatedData[columnId][month.id] = cell.value;
				} else {
					const previousValue = updatedFormatedData[columnId]?.[month.id];
					if (previousValue !== undefined && previousValue !== null) {
						if (!updatedFormatedData[columnId]) {
							updatedFormatedData[columnId] = {};
						}
						updatedFormatedData[columnId][month.id] = null;
					}
				}
			});
		});
		
		setFormatedData(updatedFormatedData);
	};

	return (
		<Modal size="xl" isOpen={show} toggle={onCloseClick} centered style={{ minWidth: '90vw' }}>
			<div className="modal-content">
				<Col md="3" className="py-4" style={{ alignSelf: "center" }}>
					<Select
						value={years.find(
							(item) => item.value === year.toString(),
						)} // Certifique-se de comparar com o valor correto
						onChange={(e) => setYear(e.value)}
						options={years}
						styles={{
							menu: (base) => ({ ...base, zIndex: 9999999 }),
						}}
					/>
				</Col>
				<ModalBody>
					{columnsLoading ? (
						<div style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '200px'
						}}>
							<div>
								<span className="spinner-border spinner-border-sm mr-1" style={{ marginRight: 10 }}></span>
								Carregando...
							</div>
						</div>
					) : (
						<div style={{ 
							display: 'flex',
							justifyContent: 'center',
							width: '100%'
						}}>
							<style>
								{`
									.Spreadsheet {
										--outline-color: #c18f00;
										--outline-background-color: rgba(193, 143, 0, 0.15);
										width: 100%;
										max-width: 100%;
									}
									.Spreadsheet__table {
										width: 100% !important;
										table-layout: fixed;
									}
									.Spreadsheet__table th:first-child,
									.Spreadsheet__table td:first-child {
										width: 60px !important;
										max-width: 60px !important;
										min-width: 60px !important;
										padding: 4px 8px !important;
										text-align: center !important;
									}
									.Spreadsheet__header {
										min-width: 0 !important;
										width: auto !important;
										max-width: none !important;
										white-space: normal !important;
										word-wrap: break-word !important;
										font-size: ${columnLabels.length > 8 ? '11px' : '13px'};
										padding: 4px !important;
									}
									.Spreadsheet__cell {
										min-width: 0 !important;
										width: auto !important;
										max-width: none !important;
										padding: 4px !important;
									}
									.Spreadsheet__cell--readonly {
										background-color: #f5f5f5 !important;
										cursor: not-allowed !important;
										opacity: 0.7;
									}
									.Spreadsheet__cell--readonly:hover {
										background-color: #e9e9e9 !important;
									}
									.Spreadsheet__data-editor {
										border: none !important;
										outline: none !important;
									}
								`}
							</style>
						<div ref={spreadsheetRef}>
							<Spreadsheet 
								data={spreadsheetData}
								columnLabels={columnLabels}
								rowLabels={rowLabels}
								onChange={handleSpreadsheetChange}
								onSelect={handleSelect}
							/>
						</div>
						</div>
					)}

					<div
						key="close"
						className="hstack gap-2 justify-content-end"
						style={{ marginTop: '20px' }}
					>
						<Button
							type="button"
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
							}}
							onClick={onCalculateSave}
							disabled={columnsLoading}
						>
							{loading ? (
								<span
									className="spinner-border spinner-border-sm mr-1"
									style={{ marginRight: 10 }}
								></span>
							) : null}
							<Calculator size={16} /> Calcular
						</Button>
						<Button
							type="button"
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
							}}
							onClick={onCloseClick}
						>
							<SignOut size={16} /> Fechar
						</Button>
					</div>
				</ModalBody>
			</div>
		</Modal>
	);
};

export default Launch;
